import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Radio } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { listenForWakeWord, isWakeWordAvailable } from "@/lib/wakeWord";
import { speakForge, startRecordingForWhisper } from "@/lib/companionVoice";
import { matchIntent } from "@/lib/intents";

const KEY_ENABLED    = "forge_wake_enabled";
const KEY_EVERYWHERE = "forge_wake_everywhere";

const GREETING = "What can I do for you today?";
const RECORDING_MS = 6000;


/**
 * Alexa-style wake-word controller. Mounted globally at App level.
 * - Enabled via localStorage "forge_wake_enabled".
 * - By default only active on /companion; enable "forge_wake_everywhere" to run site-wide.
 * - Requires logged-in user (mic + companion chat both need auth).
 * - Uses fuzzy match on "forge" / "hey forge" / "ok forge".
 * - On wake: chime → speak greeting → 6s Whisper recording → chat → speak reply → resume.
 */
export function WakeWordController() {
  const { user } = useAuth();
  const location = useLocation();
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY_ENABLED) === "1");
  const [everywhere, setEverywhere] = useState(() => localStorage.getItem(KEY_EVERYWHERE) === "1");
  const [status, setStatus] = useState("idle");   // "idle" | "listening" | "recording" | "thinking" | "speaking"
  const listenerRef = useRef(null);

  // Sync toggles from other tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY_ENABLED)    setEnabled(e.newValue === "1");
      if (e.key === KEY_EVERYWHERE) setEverywhere(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const shouldRun = Boolean(
    user &&
    enabled &&
    isWakeWordAvailable() &&
    (everywhere || location.pathname === "/companion")
  );

  const handleWake = useCallback(async () => {
    try {
      toast("Forge is listening…", { icon: "🎙", duration: 2500, id: "wake-active" });
      setStatus("speaking");
      await new Promise((resolve) => {
        speakForge(GREETING, { voice: localStorage.getItem("forge_voice") || "nova", onEnd: resolve })
          .catch(resolve);   // fall through even if TTS fails
      });

      setStatus("recording");
      let recorder;
      try {
        recorder = await startRecordingForWhisper();
      } catch (e) {
        toast.error("Mic permission denied");
        setStatus("idle");
        listenerRef.current?.resume();
        return;
      }
      // Give the user 6 seconds to speak, then transcribe.
      await new Promise((r) => setTimeout(r, RECORDING_MS));
      let transcript = "";
      try {
        transcript = await recorder.stop();
      } catch (e) {
        console.debug("[wake] transcribe failed", e);
      }

      if (!transcript || transcript.trim().length < 2) {
        toast("Didn't catch that — say Hey Forge to try again", { duration: 3000 });
        setStatus("idle");
        listenerRef.current?.resume();
        return;
      }

      // Send to Forge and speak reply — but first try the fast intent path
      setStatus("thinking");
      let reply = "";
      let intentHit = null;
      try {
        intentHit = await matchIntent(transcript);
      } catch (err) {
        console.debug("[wake] intent match threw", err);
      }
      if (intentHit) {
        reply = intentHit.reply;
      } else {
        try {
          const { data } = await api.post("/companion/chat", { message: transcript });
          reply = data?.content || data?.reply || data?.message || "Sorry, no response.";
        } catch (err) {
          reply = "I couldn't reach the server right now.";
          console.debug("[wake] chat failed", err);
        }
      }

      setStatus("speaking");
      await new Promise((resolve) => {
        speakForge(reply, { voice: localStorage.getItem("forge_voice") || "nova", onEnd: resolve })
          .catch(resolve);
      });

      setStatus("idle");
      listenerRef.current?.resume();
    } catch (err) {
      console.debug("[wake] handleWake threw", err);
      setStatus("idle");
      listenerRef.current?.resume();
    }
  }, []);

  // Start/stop listener based on shouldRun
  useEffect(() => {
    if (!shouldRun) {
      listenerRef.current?.stop();
      listenerRef.current = null;
      setStatus("idle");
      return;
    }
    setStatus("listening");
    listenerRef.current = listenForWakeWord({
      onWake: handleWake,
      onError: (code) => {
        if (code === "not-allowed" || code === "service-not-allowed") {
          toast.error("Microphone blocked. Enable it in browser settings to use wake mode.");
          setEnabled(false);
          localStorage.setItem(KEY_ENABLED, "0");
        }
      },
    });
    return () => {
      listenerRef.current?.stop();
      listenerRef.current = null;
    };
  }, [shouldRun, handleWake]);

  // Kill switch on Escape (privacy safeguard)
  useEffect(() => {
    if (!shouldRun) return;
    const onKey = (e) => {
      if (e.key === "Escape" && status !== "idle") {
        listenerRef.current?.stop();
        listenerRef.current = null;
        setEnabled(false);
        localStorage.setItem(KEY_ENABLED, "0");
        toast("Wake mode killed", { duration: 2000 });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shouldRun, status]);

  if (!shouldRun) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-3 py-2 rounded-full
                 border border-[var(--accent)]/50 bg-black/80 backdrop-blur-sm shadow-lg
                 text-[10px] font-mono select-none pointer-events-none"
      data-testid="wake-word-indicator"
      data-status={status}>
      {status === "listening" && (
        <>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent)] opacity-75"/>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--accent)]"/>
          </span>
          <Radio size={11} className="text-[var(--accent)]"/>
          <span className="text-[var(--accent)]">Say “Hey Forge”</span>
        </>
      )}
      {status === "recording" && <RecordingWithCountdown durationMs={RECORDING_MS}/>}
      {status === "thinking" && <span className="text-[var(--text-secondary)]">Thinking…</span>}
      {status === "speaking" && <span className="text-[var(--accent)]">Forge is talking…</span>}
    </div>
  );
}


/** Toggle UI. Renders two switches: Enable Wake mode + Extend everywhere.
 *  Placed in the Companion page header. Stores state in localStorage. */
export function WakeWordToggle({ compact = false }) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(KEY_ENABLED) === "1");
  const [everywhere, setEverywhere] = useState(() => localStorage.getItem(KEY_EVERYWHERE) === "1");
  const [supported, setSupported] = useState(true);

  useEffect(() => { setSupported(isWakeWordAvailable()); }, []);

  const toggleEnabled = () => {
    if (!supported) {
      toast.error("Your browser doesn't support wake-word listening. Chrome or Edge required.");
      return;
    }
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(KEY_ENABLED, next ? "1" : "0");
    if (next) toast("Wake mode ON — say “Hey Forge”", { duration: 3000, icon: "🎙" });
  };

  const toggleEverywhere = () => {
    const next = !everywhere;
    setEverywhere(next);
    localStorage.setItem(KEY_EVERYWHERE, next ? "1" : "0");
  };

  return (
    <div className={`flex items-center gap-1.5 ${compact ? "" : ""}`} data-testid="wake-word-controls">
      <button data-testid="wake-toggle-enabled"
        onClick={toggleEnabled}
        aria-pressed={enabled}
        title={
          !supported ? "Not supported in this browser"
            : enabled ? "Wake mode ON — say Hey Forge · Esc to kill"
            : "Enable wake mode"
        }
        className={`p-2 border rounded transition-colors flex items-center gap-1 text-[10px] font-mono ${
          enabled
            ? "!border-[var(--accent)] !text-[var(--accent)] bg-[var(--accent)]/10"
            : "border-white/10 hover:border-[var(--accent)] hover:text-[var(--accent)]"
        } ${!supported ? "opacity-50 cursor-not-allowed" : ""}`}>
        <Radio size={13}/>
        <span className="hidden sm:inline">Wake</span>
      </button>

      {enabled && (
        <button data-testid="wake-toggle-everywhere"
          onClick={toggleEverywhere}
          aria-pressed={everywhere}
          title={everywhere ? "Listening on every page" : "Listening only on Companion — click to extend everywhere"}
          className={`px-2 py-1 border rounded text-[9px] font-mono uppercase tracking-wide ${
            everywhere
              ? "!border-[var(--accent)] !text-[var(--accent)] bg-[var(--accent)]/10"
              : "border-white/10 text-[var(--text-muted)] hover:border-[var(--accent)]"
          }`}>
          {everywhere ? "Everywhere" : "Companion"}
        </button>
      )}
    </div>
  );
}



/** Recording indicator with an SVG countdown ring around the mic icon.
 *  The ring fills over `durationMs` via a pure CSS transition. A small
 *  digit next to it shows whole seconds remaining. */
function RecordingWithCountdown({ durationMs = 6000 }) {
  const [secondsLeft, setSecondsLeft] = React.useState(Math.ceil(durationMs / 1000));
  const [progress, setProgress] = React.useState(0);   // 0..1

  React.useEffect(() => {
    const startedAt = performance.now();
    // Kick the ring into motion on next frame so CSS transition animates from 0 → full
    requestAnimationFrame(() => setProgress(1));
    const iv = setInterval(() => {
      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(0, durationMs - elapsed);
      setSecondsLeft(Math.ceil(remaining / 1000));
      if (remaining <= 0) clearInterval(iv);
    }, 250);
    return () => clearInterval(iv);
  }, [durationMs]);

  const size = 22;
  const stroke = 2;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - progress);
  const seconds = durationMs / 1000;

  return (
    <>
      <span className="relative flex items-center justify-center" style={{ width: size, height: size }}
        data-testid="wake-recording-ring">
        {/* Track */}
        <svg width={size} height={size} className="absolute" aria-hidden="true">
          <circle cx={size/2} cy={size/2} r={r} fill="none"
            stroke="rgba(239,68,68,0.2)" strokeWidth={stroke}/>
          <circle cx={size/2} cy={size/2} r={r} fill="none"
            stroke="var(--loss)" strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ transition: `stroke-dashoffset ${seconds}s linear` }}/>
        </svg>
        <Mic size={10} className="text-[var(--loss)] relative"/>
      </span>
      <span className="text-[var(--loss)]" data-testid="wake-recording-label">
        Recording · <span data-testid="wake-countdown">{secondsLeft}s</span>
      </span>
    </>
  );
}
